/**
 * Admin · Tabbed Section Navigation (Premium Polish)
 * ---------------------------------------------------
 * Wiederverwendbare Tab-Leiste für Section-Editor-Seiten. Optik: Rolex/Chanel-
 * Backend-Feel — Jost UPPERCASE, gold-getönte States, Glass-Sticky.
 *
 * Design-Vorgaben (nicht anfassen):
 *  - Font: `Jost` (via @fontsource, self-hosted, DSGVO-clean).
 *  - Labels: 12 px, UPPERCASE, `letter-spacing: 0.08em`, Weight 500.
 *  - „Alle"-Variante: 13 px, Weight 600 (auch inaktiv).
 *  - Aktiv: Text + Icon Gold `#C6A962`, Weight 600, 2 px Gold-Bottom-Border
 *    direkt am Item (wrap-safe).
 *  - Inaktiv: Text + Icon Muted `#8A7E6D`.
 *  - Hover: Bronze `#B89B5A` Text, subtiler `bg-[#C6A962]/5` Wash.
 *  - Sticky-Glass: `backdrop-blur-md`, `bg-[#FFFDF9]/80`, dezenter Shadow.
 *  - Bottom-Border der Bar: `#D8CDBF` bei 40 % Opacity.
 *
 * A11y:
 *  - `role="tablist"`, `role="tab"`, `aria-selected`, Roving-Tabindex.
 *  - Tastatur: ←/→ / Home / End, Enter/Space (nativ via Button).
 *  - Focus: 2 px Gold-Ring mit Paper-Offset.
 *
 * Layout:
 *  - Mobile: `flex-nowrap overflow-x-auto snap-x`.
 *  - Desktop (≥ md): `flex-wrap` (wrap-safe Underline).
 */
import { useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";

export type Tab = {
    id: string;
    label: string;
    /** lucide-Icon (o. Ä.) links vom Label. */
    icon?: ReactNode;
};

type Props = {
    tabs: Tab[];
    activeId: string;
    onChange: (id: string) => void;
    /** Tab-IDs mit ungespeicherten Änderungen → Gold-Dot rechts vom Label. */
    dirtyIds?: ReadonlySet<string>;
    /** data-testid des Wrappers. */
    testId?: string;
    /** aria-label des Tablists. */
    ariaLabel?: string;
    /** ID des visuell abgesetzten „Alle"-Tabs (13 px, Weight 600). */
    allTabId?: string;
};

const COLORS = {
    paperTranslucent: "rgba(255, 253, 249, 0.8)",
    borderBottom: "rgba(216, 205, 191, 0.4)",
    gold: "#C6A962",
    goldHover: "#B89B5A",
    muted: "#8A7E6D",
} as const;

export default function TabNavigation({
    tabs, activeId, onChange, dirtyIds, testId, ariaLabel = "Bearbeitungs-Sektionen",
    allTabId,
}: Props) {
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    // Aktiven Tab bei Wechsel horizontal in Sicht scrollen (Mobile).
    useEffect(() => {
        const el = tabRefs.current[activeId];
        if (!el) return;
        try {
            el.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
        } catch {
            /* ältere Browser */
        }
    }, [activeId]);

    const handleKey = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const idx = tabs.findIndex((t) => t.id === activeId);
        if (idx < 0) return;
        let next = idx;
        if (e.key === "ArrowRight") next = (idx + 1) % tabs.length;
        else if (e.key === "ArrowLeft") next = (idx - 1 + tabs.length) % tabs.length;
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = tabs.length - 1;
        else return;
        e.preventDefault();
        const nextTab = tabs[next];
        if (nextTab) {
            onChange(nextTab.id);
            requestAnimationFrame(() => tabRefs.current[nextTab.id]?.focus());
        }
    }, [activeId, tabs, onChange]);

    return (
        <div
            className={[
                "sticky top-0 z-30 -mx-6 md:-mx-8 px-4 md:px-6",
                "backdrop-blur-md",
                "shadow-[0_1px_3px_rgba(0,0,0,0.04)]",
            ].join(" ")}
            style={{
                background: COLORS.paperTranslucent,
                borderBottom: `1px solid ${COLORS.borderBottom}`,
                fontFamily: '"Jost", "Inter", system-ui, sans-serif',
            }}
            data-testid={testId ?? "admin-tab-navigation"}
        >
            <div
                role="tablist"
                aria-label={ariaLabel}
                onKeyDown={handleKey}
                className={[
                    // Mobile: eine Zeile, horizontales Scroll
                    "flex flex-nowrap gap-1 overflow-x-auto snap-x snap-mandatory hf-tabnav-scroll",
                    // Desktop: Wrap-fähig
                    "md:flex-wrap md:overflow-visible",
                    // Vertikaler Rhythmus
                    "py-2",
                ].join(" ")}
            >
                {tabs.map((t) => {
                    const active = t.id === activeId;
                    const dirty = dirtyIds?.has(t.id) ?? false;
                    const isAll = allTabId && t.id === allTabId;
                    const color = active ? COLORS.gold : COLORS.muted;
                    return (
                        <button
                            key={t.id}
                            ref={(el) => { tabRefs.current[t.id] = el; }}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            aria-controls={`tabpanel-${t.id}`}
                            id={`tab-${t.id}`}
                            tabIndex={active ? 0 : -1}
                            onClick={() => onChange(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            data-active={active ? "true" : "false"}
                            data-variant={isAll ? "all" : "section"}
                            className={[
                                "hf-admin-tab group relative shrink-0 md:shrink snap-start whitespace-nowrap",
                                "inline-flex items-center gap-1.5",
                                "px-4 py-3 rounded-sm",
                                "uppercase tracking-[0.08em] leading-none",
                                "transition-all duration-200 outline-none",
                                // Font-Size + Weight je nach Variante/State
                                isAll ? "text-[13px]" : "text-xs",
                                active || isAll ? "font-semibold" : "font-medium",
                            ].join(" ")}
                            style={{ color }}
                        >
                            {t.icon && (
                                <span
                                    aria-hidden="true"
                                    className="inline-flex items-center transition-colors duration-200"
                                    style={{ color }}
                                >
                                    {t.icon}
                                </span>
                            )}
                            <span>{t.label}</span>
                            {dirty && (
                                <span
                                    aria-hidden="true"
                                    title="Ungespeicherte Änderungen"
                                    className="inline-block w-1.5 h-1.5 rounded-full"
                                    style={{ background: COLORS.gold }}
                                />
                            )}
                            {/* Aktiver Underline (Gold, 2 px) — am Item, wrap-safe. */}
                            <span
                                aria-hidden="true"
                                className="absolute left-3 right-3 -bottom-[2px] h-[2px] transition-opacity duration-200"
                                style={{
                                    background: COLORS.gold,
                                    opacity: active ? 1 : 0,
                                }}
                            />
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
